import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { canSearchTargetsOf } from "permissions/_api/permission-targets-api-service";
import type { RecordTarget, RecordTargetSearch } from "permissions/_views/people/use-record-target-search";
import { useEffect, useRef } from "react";

/**
 * The target step of Add access, for a per-record kind: find one record by name and choose it
 * (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "Adding, removing, changing").
 *
 * Records are named with the kind's `targetNoun`, so the step says "conversation", never "forum".
 * Each result is two lines, as the header search draws its results. A record the person already holds
 * a direct grant on is marked, and choosing it changes that grant rather than adding a second.
 *
 * Records are found through the kind's admin-gated targets search (#6174, #6795), which answers every
 * record of the organization by name, whether or not the administrator may open it. A kind no centre
 * searches says so rather than offering a search that finds nothing.
 */

export const TARGET_HELD = "Held";

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** What the step says for a per-record kind whose records cannot be found by name yet. */
export function notFindableYet(noun: string): string {
  return `A ${noun} cannot be found by name here yet.`;
}

/** The noun a per-record kind's records go by: "document", "conversation". */
export function targetNounOf(kind: PermissionKind): string {
  return kind.targetNoun ?? "record";
}

export type AddAccessTargetStepProps = Readonly<{
  personName: string;
  kind: PermissionKind;
  /** The name typed, and what it found. The dialog runs the search, so a chosen record stays current. */
  text: string;
  search: RecordTargetSearch;
  onText: (text: string) => void;
  chosen?: RecordTarget;
  onChoose: (target: RecordTarget) => void;
}>;

export default function AddAccessTargetStep(props: AddAccessTargetStepProps) {
  const { personName, kind, text, search, onText, chosen, onChoose } = props;
  const searchRef = useRef<HTMLInputElement>(null);
  const noun = targetNounOf(kind);
  const words = text.trim();

  // Focus moves here, the first field left to fill, as soon as a per-record kind is chosen. The step is
  // keyed by kind, so choosing another one mounts it again.
  useEffect(() => searchRef.current?.focus(), []);

  if (!canSearchTargetsOf(kind.resourceType)) {
    return (
      <Alert severity="info" aria-label={`Which ${noun}`}>
        {notFindableYet(noun)}
      </Alert>
    );
  }

  let status: string | undefined;
  if (!words) status = `Type a ${noun}'s name to find it.`;
  else if (search.isSearching && !search.targets) status = "Searching…";
  else if (search.targets?.length === 0) status = `No ${noun} matches "${words}".`;

  return (
    <Box component="section" aria-label={`Which ${noun}`} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <TextField
        inputRef={searchRef}
        fullWidth
        label={`Find a ${noun}`}
        value={text}
        onChange={(event) => onText(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      {search.error && <Alert severity="error">{`The ${noun} search failed. ${search.error}`}</Alert>}
      {status && (
        <Typography variant="body2" color="text.secondary" aria-live="polite">
          {status}
        </Typography>
      )}
      {search.targets && search.targets.length > 0 && (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, maxHeight: { sm: 280 }, overflowY: { sm: "auto" } }}>
          <List dense disablePadding aria-label={`${capitalized(noun)}s`}>
            {search.targets.map((target) => (
              <ListItemButton
                key={target.id}
                selected={chosen?.id === target.id}
                onClick={() => onChoose(target)}
                sx={{ minHeight: 44, gap: 1, alignItems: "center" }}
              >
                <ListItemText
                  primary={target.name}
                  secondary={target.held ? `${personName} already holds this ${noun}` : capitalized(noun)}
                  slotProps={{ primary: { sx: { fontWeight: 500, overflowWrap: "anywhere" } } }}
                />
                {target.held && <Chip size="small" variant="outlined" label={TARGET_HELD} />}
              </ListItemButton>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
